import * as Sentry from "@sentry/react";

type Tags = Record<string, string | number | boolean>;

function safe(fn: () => void) {
  try { fn(); } catch { /* Sentry not initialized or metrics unavailable */ }
}

export const metrics = {
  count(key: string, value = 1, tags?: Tags) {
    safe(() => Sentry.metrics.count(key, value, { tags }));
  },
  gauge(key: string, value: number, tags?: Tags) {
    safe(() => Sentry.metrics.gauge(key, value, { tags }));
  },
  distribution(key: string, value: number, tags?: Tags) {
    safe(() => Sentry.metrics.distribution(key, value, { tags }));
  },
};

// ── Auth ──────────────────────────────────────────────────────────────────────

export function trackLogin(method: "email" | "demo" | "otp") {
  metrics.count("auth.login", 1, { method });
}

export function trackLoginFailed(method: "email" | "demo" | "otp") {
  metrics.count("auth.login_failed", 1, { method });
}

export function trackSignup() {
  metrics.count("auth.signup", 1);
}

export function trackDemoRequest() {
  metrics.count("auth.demo_request", 1);
}

export function trackDemoConfirmed() {
  metrics.count("auth.demo_confirmed", 1);
}

// ── Navigation ────────────────────────────────────────────────────────────────

export function trackPageView(route: string) {
  metrics.count("page.view", 1, { route });
}

// ── CRM / Leads ───────────────────────────────────────────────────────────────

export function trackLeadCreated(dealValue?: number | null) {
  metrics.count("lead.created", 1);
  if (dealValue != null && dealValue > 0) {
    metrics.distribution("lead.deal_value", dealValue);
  }
}

export function trackLeadStageChanged(fromStage: string, toStage: string) {
  metrics.count("lead.stage_changed", 1, { from_stage: fromStage, to_stage: toStage });
  if (toStage === "won") metrics.count("lead.won", 1);
  if (toStage === "lost") metrics.count("lead.lost", 1);
}

export function trackLeadActivityLogged(type: string) {
  metrics.count("lead.activity_logged", 1, { type });
}

export function trackLeadDeleted() {
  metrics.count("lead.deleted", 1);
}

export function trackLeadPipeline(totalLeads: number, pipelineValue: number) {
  metrics.gauge("lead.count", totalLeads);
  metrics.gauge("lead.pipeline_value_usd", pipelineValue);
}

// ── API timing ────────────────────────────────────────────────────────────────

export function trackApiCall(method: string, path: string, durationMs: number, ok: boolean) {
  const route = path.replace(/\/[a-zA-Z0-9_-]{8,}/g, "/:id");
  metrics.distribution("api.response_time_ms", durationMs, {
    method: method.toLowerCase(),
    route,
    status: ok ? "ok" : "error",
  });
}
